import { useState, type FormEvent } from 'react';
import { ApiFailure, api } from '../api.ts';

/**
 * The door. Email and password to `POST /auth/login`, which answers with an
 * opaque bearer token.
 *
 * ONE MESSAGE FOR EVERY LOGIN FAILURE, and it is the server's — "Wrong email
 * or password." The server deliberately refuses to distinguish a missing
 * account from a wrong password (`routes/auth.ts` even burns matching CPU time
 * so the two cannot be told apart by latency), and a page that helpfully
 * elaborated would undo that in one line.
 *
 * WHETHER THIS ACCOUNT IS AN ADMIN IS NOT DECIDED HERE. Any real credential
 * gets a token; `App` then asks the server what that token can do. Refusing
 * the login itself would tell a labeler their password was wrong, which is a
 * lie, and would make "I can sign in on my phone but not here" unexplainable.
 */
export function SignIn({ onToken }: { onToken: (token: string) => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.login(email.trim(), password);
      onToken(token);
    } catch (err) {
      setError(
        err instanceof ApiFailure ? err.message : 'Could not sign in. Try again in a moment.',
      );
      setBusy(false);
    }
    // No `setBusy(false)` on success: this component is about to be replaced,
    // and re-enabling the button first lets a fast second click fire a second
    // login against a session that already exists.
  }

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <div className="signin-mark">
          <span className="wordmark">taginai</span>
          <span className="wordmark-sub">admin</span>
        </div>

        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}

        <label className="field">
          <span className="field-label">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={busy}
          />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={busy}
          />
        </label>

        <button className="btn-primary" type="submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
